import {
	ButtonWithSpinner,
	DatePicker,
	FileUpload,
	FormFieldControl,
	SlideoutMenu,
	ToastCustom,
} from "@components";
import { zodResolver } from "@hookform/resolvers/zod";
import { CalendarDate, getLocalTimeZone, today } from "@internationalized/date";
import { useConfirmModal } from "@providers";
import { FileAttachment02 } from "@untitledui/icons";
import { useEffect, useMemo } from "react";
import type { DateValue } from "react-aria-components";
import { Controller, useForm } from "react-hook-form";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { z } from "zod/v3";
import { Select, type SelectItemType } from "@/components/base/select/select";
import { TextArea } from "@/components/base/textarea/textarea";
import { DOCUMENT_TYPE_OPTIONS } from "@/features/vehicle/constants/document-types.constant";
import type { VehicleDocument } from "@/features/vehicle/types/vehicle-document.types";
import { useVehicleDocumentUpload } from "../hooks";

const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024; // 10MB

const buildVehicleDocumentFormSchema = (isEditing: boolean) =>
	z
		.object({
			documentTypeId: z.string().min(1, "Tipo de documento obrigatório"),
			file: z
				.any()
				.refine(
					(file) =>
						!file || (file instanceof File && file.size <= MAX_FILE_SIZE_BYTES),
					{
						message: "Arquivo obrigatório e com no máximo 10MB",
						path: ["file"],
					}
				)
				.nullable()
				.optional(),
			paymentDate: z.any().nullable().optional(),
			renewalDate: z.any().nullable(),
			description: z.string().max(100, "Descrição muito longa").optional(),
		})
		.superRefine((values, ctx) => {
			if (
				(values.documentTypeId === "1" || values.documentTypeId === "2") &&
				!values.renewalDate
			) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					path: ["renewalDate"],
					message: "Próxima renovação é obrigatória",
				});
			}

			const paymentIso = dateValueToIso(values.paymentDate as DateValue | null);
			const renewalIso = dateValueToIso(values.renewalDate as DateValue | null);
			if (paymentIso && renewalIso && renewalIso < paymentIso) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					path: ["renewalDate"],
					message:
						"A data de renovação deve ser igual ou posterior à data de pagamento.",
				});
			}

			if (!(isEditing || values.file)) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					path: ["file"],
					message: "Arquivo é obrigatório",
				});
			}

			if (values.documentTypeId === "3" && !values.description) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					path: ["description"],
					message: "Descrição é obrigatória",
				});
			}
		});

type VehicleDocumentFormValues = z.infer<
	ReturnType<typeof buildVehicleDocumentFormSchema>
>;

function parseDateString(value: string): CalendarDate | null {
	if (!value) {
		return null;
	}
	const [y, m, d] = value.split("-").map(Number);
	if (Number.isNaN(y) || Number.isNaN(m) || Number.isNaN(d)) {
		return null;
	}
	return new CalendarDate(y, m, d);
}

function dateValueToIso(value: DateValue | null | undefined): string {
	if (value === null || value === undefined) {
		return "";
	}
	const d = value as CalendarDate;
	return `${d.year}-${String(d.month).padStart(2, "0")}-${String(d.day).padStart(2, "0")}`;
}

/** Verifica se algum campo do formulário mudou em relação aos valores iniciais (útil em edição). */
function hasFormChanges(
	current: VehicleDocumentFormValues,
	initial: VehicleDocumentFormValues
): boolean {
	if (current.file !== initial.file) {
		return true;
	}
	if (
		dateValueToIso(current.paymentDate as DateValue | null) !==
		dateValueToIso(initial.paymentDate as DateValue | null)
	) {
		return true;
	}
	if (
		dateValueToIso(current.renewalDate as DateValue | null) !==
		dateValueToIso(initial.renewalDate as DateValue | null)
	) {
		return true;
	}
	if (
		(current.description ?? "").trim() !== (initial.description ?? "").trim()
	) {
		return true;
	}
	return false;
}

/**
 * Valida se todos os campos obrigatórios estão preenchidos conforme o schema:
 * - documentTypeId: sempre obrigatório
 * - file: obrigatório em criação; opcional em edição
 * - renewalDate: obrigatório quando tipo é CRLV (1) ou Seguro (2)
 * - description: obrigatório quando tipo é Outro (3)
 */
function areRequiredFieldsFilled(
	values: VehicleDocumentFormValues,
	isEditing: boolean
): boolean {
	if (!values.documentTypeId || values.documentTypeId === "") {
		return false;
	}
	if (!(isEditing || values.file)) {
		return false;
	}
	if (
		(values.documentTypeId === "1" || values.documentTypeId === "2") &&
		!values.renewalDate
	) {
		return false;
	}
	if (
		values.documentTypeId === "3" &&
		!(
			typeof values.description === "string" && values.description.trim() !== ""
		)
	) {
		return false;
	}
	return true;
}

type VehicleDocumentDrawerProps = {
	isOpen: boolean;
	onClose: () => void;
	documentId?: string | null;
	initialDocument?: VehicleDocument | null;
	onSubmit?: (document: VehicleDocument) => void;
};

export function VehicleDocumentDrawer({
	isOpen,
	onClose,
	documentId = null,
	initialDocument = null,
}: VehicleDocumentDrawerProps) {
	const isEditing =
		documentId !== null &&
		documentId !== undefined &&
		initialDocument !== null &&
		initialDocument !== undefined;
	const [searchParams] = useSearchParams();
	const vehicleId = searchParams.get("id") || "";

	const { mutate: uploadVehicleDocument, isPending: isUploading } =
		useVehicleDocumentUpload(onClose);

	const { confirm } = useConfirmModal();

	const defaultValues: VehicleDocumentFormValues = useMemo(() => {
		if (!initialDocument) {
			return {
				documentTypeId: "",
				file: null,
				paymentDate: null,
				renewalDate: null,
				description: "",
			};
		}

		const opt = DOCUMENT_TYPE_OPTIONS.find(
			(o) => o.id === initialDocument.type
		);

		return {
			documentTypeId: opt ? String(opt.id) : "",
			file: null,
			paymentDate: parseDateString(
				initialDocument.paymentDate ?? initialDocument.createdAt
			),
			renewalDate: parseDateString(initialDocument.renewalDate),
			description: initialDocument.description ?? "",
		};
	}, [initialDocument]);

	const formSchema = useMemo(
		() => buildVehicleDocumentFormSchema(isEditing),
		[isEditing]
	);

	const {
		control,
		getValues,
		handleSubmit,
		reset,
		setValue,
		formState: { errors, isSubmitting },
		watch,
	} = useForm<VehicleDocumentFormValues>({
		resolver: zodResolver(formSchema),
		defaultValues,
	});

	const watchedValues = watch();
	const isFormValid = areRequiredFieldsFilled(watchedValues, isEditing);

	const watchDocumentTypeId = watch("documentTypeId");

	const isDescriptionVisible = watchDocumentTypeId === "3";

	useEffect(() => {
		if (isOpen) {
			reset(defaultValues);
		}
	}, [isOpen, defaultValues, reset]);

	const handleClose = () => {
		reset(defaultValues);
		onClose();
	};

	const confirmEditIfNeeded = async (file: File | null) => {
		if (file) {
			const result = await confirm({
				title: "Tem certeza de que deseja substituir o documento cadastrado?",
				description: "Ao substituí-lo, você não poderá recuperá-lo.",
				cancelText: "Cancelar",
				confirmText: "Salvar Edição de Documento",
				variant: "warning",
			});
			if (!result) {
				return false;
			}
		} else {
			const result = await confirm({
				title: "Confirma a edição do documento?",
				description: "Ao confirmar, o documento é atualizado.",
				cancelText: "Cancelar",
				confirmText: "Confirmar Edição",
				variant: "warning",
			});
			if (!result) {
				return false;
			}
		}
		return true;
	};

	const onValidSubmit = async (payload: VehicleDocumentFormValues) => {
		const { documentTypeId, file, renewalDate, paymentDate, description } =
			payload;

		if (isEditing) {
			const canProceed = await confirmEditIfNeeded(file);
			if (!canProceed) {
				return;
			}
		}

		const data = {
			vehicleId,
			type: Number(documentTypeId),
			fileName: file?.name,
			contentType: file?.type,
			...(file && { file: file as File }),
			...(renewalDate && {
				renewalDate: renewalDate.toString("YYYY-MM-DD"),
			}),
			...(paymentDate && {
				paymentDate: paymentDate.toString("YYYY-MM-DD"),
			}),
			...(description && documentTypeId === "3" && { description }),
			...(documentId && { documentId }),
		};

		uploadVehicleDocument(data);
	};

	const title = isEditing
		? "Edição de Documento"
		: "Novo Cadastro de Documento";
	const subtitle = isEditing
		? "Edite as informações abaixo para alterar o documento cadastrado"
		: "Preencha as informações do documento para adicionar ao sistema";
	const submitLabel = isEditing
		? "Salvar Edição de Documento"
		: "Salvar Documento";

	const handleSlideoutOpenChange = async (open: boolean) => {
		if (open) {
			return;
		}
		const hasChanges = isEditing && hasFormChanges(getValues(), defaultValues);
		if (hasChanges) {
			const result = await confirm({
				title: "Tem certeza de que deseja sair?",
				description: "As alterações não foram salvas.",
				cancelText: "Cancelar",
				confirmText: "Sair",
				variant: "warning",
			});
			if (!result) {
				return;
			}
		}
		handleClose();
	};

	return (
		<SlideoutMenu
			className="w-full max-w-[626px]"
			isOpen={isOpen}
			onOpenChange={handleSlideoutOpenChange}
		>
			{({ close }) => (
				<>
					<SlideoutMenu.Header onClose={close}>
						<div className="flex flex-col gap-1 pr-8">
							<h2 className="font-semibold text-lg text-primary">{title}</h2>
							<p className="text-fg-secondary text-sm">{subtitle}</p>
						</div>
					</SlideoutMenu.Header>
					<SlideoutMenu.Content>
						<div className="flex flex-col gap-4">
							<Controller
								control={control}
								name="documentTypeId"
								render={({ field }) => {
									const handleChange = (value: string | number | null) => {
										field.onChange(value !== null ? String(value) : "");
									};

									const selectedValue =
										typeof field.value === "string" && field.value !== ""
											? Number(field.value)
											: null;

									return (
										<FormFieldControl
											disabled={isEditing}
											error={errors.documentTypeId?.message as string}
											htmlFor="documentTypeId"
											isRequired
											label="Tipo de Documento"
										>
											<Select
												className="w-full"
												data-invalid={!!errors.documentTypeId?.message}
												isDisabled={isEditing}
												isInvalid={!!errors.documentTypeId?.message}
												items={
													DOCUMENT_TYPE_OPTIONS as unknown as SelectItemType[]
												}
												onChange={handleChange}
												placeholder="Selecione o tipo"
												value={selectedValue}
											>
												{(item) => (
													<Select.Item id={item.id} key={item.id}>
														{item.label}
													</Select.Item>
												)}
											</Select>
										</FormFieldControl>
									);
								}}
							/>

							{!!isEditing && (
								<FormFieldControl
									htmlFor="fileOld"
									isRequired
									label="Documento enviado"
								>
									<span className="flex items-center gap-2 text-secondary text-sm">
										<FileAttachment02 className="inline size-3.5 align-middle" />{" "}
										{initialDocument?.fileName ?? "Nome_Arquivo.PDF"}
									</span>
								</FormFieldControl>
							)}

							<Controller
								control={control}
								name="file"
								render={({ field }) => {
									const file = watch("file") as File | null;
									const isDisabled = !!file?.name;

									return (
										<FormFieldControl
											error={errors.file?.message as string}
											htmlFor="file"
											isRequired={!isEditing}
											label={isEditing ? "Enviar novo documento" : "Arquivo"}
										>
											<FileUpload.Root className="w-full max-w-full overflow-hidden">
												<FileUpload.DropZone
													accept=".png,.jpg,.jpeg,.webp,.pdf,.txt,.csv,.doc,.docx,.xls,.xlsx"
													allowsMultiple={false}
													hint={
														"Envie apenas arquivos PNG, JPG, JPEG, WEBP, PDF, TXT, CSV, DOC, DOCX, XLS, XLSX.\n" +
														`Tamanho máximo: ${MAX_FILE_SIZE_BYTES / 1024 / 1024} MB`
													}
													isDisabled={isDisabled}
													maxSize={MAX_FILE_SIZE_BYTES}
													onDropFiles={(files) => {
														field.onChange(files.length > 0 ? files[0] : null);
													}}
													onDropUnacceptedFiles={() =>
														toast(
															<ToastCustom
																description="Arquivo não aceito. Verifique o formato ou tamanho."
																title="Erro"
															/>
														)
													}
													onSizeLimitExceed={() =>
														toast(
															<ToastCustom
																description="Arquivo não aceito. Verifique o formato ou tamanho."
																title="Erro"
															/>
														)
													}
												/>
												{!!file?.name && (
													<FileUpload.List>
														<FileUpload.ListItemProgressBar
															{...file}
															key={file.name}
															name={file.name}
															onDelete={() => setValue("file", null)}
															progress={100}
															size={file.size}
														/>
													</FileUpload.List>
												)}
											</FileUpload.Root>
										</FormFieldControl>
									);
								}}
							/>

							<Controller
								control={control}
								name="paymentDate"
								render={({ field }) => {
									const maxPaymentDate = today(getLocalTimeZone());
									const handleChange = (value: DateValue | null) => {
										if (!value) {
											field.onChange(null);
											return;
										}
										if (value.compare(maxPaymentDate) > 0) {
											field.onChange(maxPaymentDate);
											return;
										}
										field.onChange(value);
									};

									return (
										<FormFieldControl
											error={errors.paymentDate?.message as string}
											htmlFor="paymentDate"
											label="Data de Pagamento"
										>
											<DatePicker
												className="w-full"
												maxValue={maxPaymentDate}
												onChange={handleChange}
												value={field.value as DateValue | null}
											/>
										</FormFieldControl>
									);
								}}
							/>

							<Controller
								control={control}
								name="renewalDate"
								render={({ field }) => {
									const handleChange = (value: DateValue | null) => {
										field.onChange(value);
									};

									const isCRLV = watchDocumentTypeId === "1";
									const isSeguro = watchDocumentTypeId === "2";
									return (
										<FormFieldControl
											error={errors.renewalDate?.message as string}
											htmlFor="renewalDate"
											isRequired={isCRLV || isSeguro}
											label="Próxima Renovação"
										>
											<DatePicker
												className="w-full"
												onChange={handleChange}
												value={field.value as DateValue | null}
											/>
										</FormFieldControl>
									);
								}}
							/>

							{isDescriptionVisible ? (
								<Controller
									control={control}
									name="description"
									render={({ field }) => {
										const handleChange = (value: string) => {
											field.onChange(value);
										};

										return (
											<FormFieldControl
												error={errors.description?.message}
												htmlFor="description"
												isRequired
												label="Descrição"
											>
												<TextArea
													maxLength={100}
													onChange={handleChange}
													placeholder="Descrição do documento que foi feita no ato do cadastro..."
													rows={3}
													value={(field.value as string | undefined) ?? ""}
												/>
											</FormFieldControl>
										);
									}}
								/>
							) : null}
						</div>
					</SlideoutMenu.Content>
					<SlideoutMenu.Footer>
						<ButtonWithSpinner
							className="w-full"
							disabled={isSubmitting || isUploading || !isFormValid}
							isLoading={isUploading}
							onClick={handleSubmit(onValidSubmit)}
							size="lg"
						>
							{submitLabel}
						</ButtonWithSpinner>
					</SlideoutMenu.Footer>
				</>
			)}
		</SlideoutMenu>
	);
}
